/**
 * 篇章内容详情抽屉（旧 components/EpisodeContentDrawer.vue）：基础信息、故事内容、故事概述、
 * 下一篇预告、生成设定、阅读统计、生成信息；故事内容 / 概述 / 生成设定可编辑。
 * 篇章数据取自所在实例的篇章列表缓存，编辑保存后随列表刷新。
 */
import { useState } from 'react'
import { Badge, Button, Card, Descriptions, Drawer, Empty, Typography } from 'antd'
import { JsonViewer } from '@/components/json-viewer/JsonViewer'
import { MarkdownView } from '@/components/MarkdownView'
import { episodeDetailLabel, episodeStatusBadge, formatDate, hasContent } from './constants'
import { useInstanceEpisodes, useUpdateEpisodeContent } from './hooks'
import { EditContentModal, type EditTarget } from './EditContentModal'
import styles from './UserInstances.module.css'

interface EpisodeContentDrawerProps {
  instanceId: number | null
  // 为 null 时抽屉关闭
  episodeId: number | null
  onClose: () => void
}

function NotSet() {
  return <Typography.Text type="secondary">（未设置）</Typography.Text>
}

export function EpisodeContentDrawer({ instanceId, episodeId, onClose }: Readonly<EpisodeContentDrawerProps>) {
  const { data: episodes } = useInstanceEpisodes(instanceId)
  const updateContent = useUpdateEpisodeContent()
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null)
  const episode = episodes?.find((item) => item.id === episodeId) ?? null

  const save = async (payload: Record<string, unknown>) => {
    if (!episode) return
    await updateContent.mutateAsync({ instanceId: episode.userThemeInstanceId, episodeId: episode.id, payload })
  }

  const editStoryText = () =>
    episode &&
    setEditTarget({
      title: '编辑故事内容',
      mode: 'text',
      rows: 20,
      maxLength: 50000,
      placeholder: '请输入故事内容',
      initialValue: episode.storyText || '',
      onSave: (value) => save({ storyText: value })
    })

  const editStorySummary = () =>
    episode &&
    setEditTarget({
      title: '编辑故事概述',
      mode: 'text',
      rows: 20,
      maxLength: 50000,
      placeholder: '请输入故事概述',
      initialValue: episode.storySummary || '',
      onSave: (value) => save({ storySummary: value })
    })

  const editFixedSetting = () =>
    episode &&
    setEditTarget({
      title: '编辑生成设定',
      mode: 'json',
      rows: 15,
      initialValue: JSON.stringify(episode.updatedFixedSetting || {}, null, 2),
      onSave: (value) => save({ updatedFixedSetting: value })
    })

  const titleWithEdit = (title: string, label: string, onClick: () => void) => (
    <span>
      {title}
      <Button type="link" size="small" aria-label={label} onClick={onClick}>
        编辑
      </Button>
    </span>
  )

  return (
    <Drawer open={episodeId !== null} title="篇章内容详情" size={900} footer={null} onClose={onClose}>
      {episode && (
        <>
          <Card title="基础信息" size="small" className={styles.sectionCard}>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="篇章ID">{episode.id}</Descriptions.Item>
              <Descriptions.Item label="篇章编号">Episode {episode.episodeNumber}</Descriptions.Item>
              <Descriptions.Item label="用户实例ID">{episode.userThemeInstanceId}</Descriptions.Item>
              <Descriptions.Item label="用户ID">{episode.userId}</Descriptions.Item>
              <Descriptions.Item label="篇章标题" span={2}>
                {episode.title || '（无标题）'}
              </Descriptions.Item>
              <Descriptions.Item label="篇章状态">
                <Badge status={episodeStatusBadge(episode.status)} text={episodeDetailLabel(episode.status)} />
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">{formatDate(episode.createdAt)}</Descriptions.Item>
            </Descriptions>
          </Card>

          <Card
            size="small"
            className={styles.sectionCard}
            title={titleWithEdit('故事内容', '编辑故事内容', editStoryText)}
          >
            {episode.storyText ? <MarkdownView content={episode.storyText} /> : <span>（无内容）</span>}
          </Card>

          <Card
            size="small"
            className={styles.sectionCard}
            title={titleWithEdit('故事概述 (Story Summary)', '编辑故事概述', editStorySummary)}
          >
            {episode.storySummary ? (
              <MarkdownView content={episode.storySummary} />
            ) : (
              <Empty description="暂无故事概述" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>

          <Card title="下一篇预告 (Next Episode Preview)" size="small" className={styles.sectionCard}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="下一篇标题 (Next Episode Title)">
                {episode.nextEpisodeTitle || '（未设置）'}
              </Descriptions.Item>
              <Descriptions.Item label="预告内容 (Teaser)">
                {episode.nextEpisodeTeaser ? <MarkdownView content={episode.nextEpisodeTeaser} /> : <NotSet />}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card
            size="small"
            className={styles.sectionCard}
            title={titleWithEdit('生成设定 (Updated Fixed Setting)', '编辑生成设定', editFixedSetting)}
          >
            {hasContent(episode.updatedFixedSetting) ? (
              <JsonViewer data={episode.updatedFixedSetting} />
            ) : (
              <Empty description="暂无生成设定数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>

          <Card title="阅读统计数据" size="small" className={styles.sectionCard}>
            {hasContent(episode.episodeReadingStats) ? (
              <JsonViewer data={episode.episodeReadingStats} />
            ) : (
              <Empty description="暂无阅读统计数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>

          <Card title="生成信息" size="small">
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="提示策略版本">{episode.promptPolicyVersion || '（未设置）'}</Descriptions.Item>
              <Descriptions.Item label="生成请求ID">{episode.generationRequestId || '（未设置）'}</Descriptions.Item>
            </Descriptions>
          </Card>
        </>
      )}

      <EditContentModal target={editTarget} onClose={() => setEditTarget(null)} />
    </Drawer>
  )
}
